/**
 * The side panel for one part, PO or container. It's driven by the URL (?part=, ?po=, ?ca=)
 * so any page can open it, Back closes it, and "Copy link" shares exactly what you see.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MonitorRow } from "@march/shared";
import { SHOW_GOLD_CREST } from "../lib/flags.ts";
import { apiGet } from "../lib/api.ts";
import { useSearchParams } from "react-router-dom";
import { partName } from "../lib/skuNames.ts";
import {
  addDays, daysBetween, emptyDays, fmt, looksHigh, projectStock, refLabel, runsOut, shortDate, unitOf, useSupply,
  type Delivery, type Line, type Row,
} from "../lib/supply.ts";
import { CATEGORY_LABEL } from "../lib/categories.ts";
import { StockChart } from "./StockTimeline.tsx";

type Kind = "part" | "po" | "ca";

export function usePanel() {
  const [params, setParams] = useSearchParams();
  const open = (kind: Kind, id: string) => {
    const next = new URLSearchParams(params);
    ["part", "po", "ca", "slip"].forEach((k) => next.delete(k));
    next.set(kind, id);
    setParams(next);
  };
  const close = () => {
    const next = new URLSearchParams(params);
    ["part", "po", "ca", "slip"].forEach((k) => next.delete(k));
    setParams(next);
  };
  return { open, close, params, setParams };
}

const NS_ACCOUNT = (import.meta.env.VITE_NETSUITE_ACCOUNT as string | undefined) ?? "";

/** A link into NetSuite's global search when the account is configured, otherwise a copy button. */
function NetSuiteButton({ query, label = "Open in NetSuite", primary = false }: { query: string; label?: string; primary?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (NS_ACCOUNT) {
    const host = NS_ACCOUNT.toLowerCase().replace(/_/g, "-");
    const href = `https://${host}.app.netsuite.com/app/common/search/ubersearchresults.nl?quicksearch=T&searchtype=Uber&Uber_NAME=${encodeURIComponent(query)}`;
    return <a className={`btn ext${primary ? " primary" : ""}`} href={href} target="_blank" rel="noreferrer">{label}</a>;
  }
  return (
    <button type="button" className={`btn${primary ? " primary" : ""}`} onClick={() => copy(query, setCopied)} title="Copy it, then paste into NetSuite's search">
      {copied ? "Copied" : `Copy “${query}” for NetSuite`}
    </button>
  );
}

function copy(text: string, done: (b: boolean) => void) {
  navigator.clipboard?.writeText(text).then(() => {
    done(true);
    setTimeout(() => done(false), 1500);
  });
}

function CopyLink() {
  const [copied, setCopied] = useState(false);
  return <button type="button" className="btn" onClick={() => copy(location.href, setCopied)}>{copied ? "Link copied" : "Copy link"}</button>;
}

export function DetailPanel() {
  const { params, close } = usePanel();
  const sku = params.get("part");
  const po = params.get("po");
  const ca = params.get("ca");
  const openKind = sku ? "part" : po ? "po" : ca ? "ca" : null;

  useEffect(() => {
    if (!openKind) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKind]);

  if (!openKind) return null;
  return (
    <>
      <div className="panel-scrim" onClick={close} aria-hidden="true" />
      <aside className="panel" role="dialog" aria-modal="true" aria-label="Details">
        {sku && <PartBody sku={sku} />}
        {po && <DeliveryBody refId={po} />}
        {ca && <DeliveryBody refId={ca} />}
      </aside>
    </>
  );
}

function Head({ title, sub }: { title: string; sub?: React.ReactNode }) {
  const { close } = usePanel();
  return (
    <div className="ph">
      <div>
        <h2>{title}</h2>
        {sub && <div className="muted">{sub}</div>}
      </div>
      <button type="button" className="close" onClick={close} aria-label="Close">✕</button>
    </div>
  );
}

// ─── part ───────────────────────────────────────────────────────

const CONFIDENCE: Record<string, string> = {
  high: "high (most past forecasts within 25%)",
  medium: "medium (most past forecasts within 50%)",
  low: "low (past forecasts often off by more than half; use judgement)",
  low_volume: "too few sales to judge",
  new: "too new to judge",
};

/**
 * Where the stock sits (Jurupa / Texas, plus Gold Crest's own stock when SHOW_GOLD_CREST is on) and the last 30 days of real use
 * next to the forecast's pace. From the /api/monitor data, which no longer has a page of its own.
 */
function Sites({ sku, type, f30 }: { sku: string; type: string; f30: number }) {
  const mk = (sub: "MP" | "GC") => ({
    queryKey: ["monitor", type, sub],
    queryFn: () => apiGet<{ rows: MonitorRow[] }>(`/api/monitor?type=${type}&subsidiary=${sub}`),
  });
  const mp = useQuery(mk("MP")).data?.rows.find((x) => x.itemid === sku);
  const gc = useQuery({ ...mk("GC"), enabled: SHOW_GOLD_CREST }).data?.rows.find((x) => x.itemid === sku);
  if (!mp && !gc) return null;
  const ratio = mp && f30 >= 1 ? mp.burn_30d / f30 : null;
  return (
    <>
      {mp && <div className="muted" style={{ marginTop: 4 }}>Jurupa {fmt(mp.qoh.loc15)} · Texas {fmt(mp.qoh.loc64)}</div>}
      {SHOW_GOLD_CREST && gc && (gc.qoh.total > 0 || gc.burn_30d > 0) && (
        <div className="muted" style={{ marginTop: 4 }}>Gold Crest (separate company): {fmt(gc.qoh.total)} in stock · used {fmt(gc.burn_30d)} in the last 30 days</div>
      )}
      {mp && (
        <div className="muted" style={{ marginTop: 4 }}>
          Used {fmt(mp.burn_30d)} in the last 30 days · the forecast's pace is {fmt(f30)}
          {ratio != null && ratio >= 1.5 && mp.burn_30d >= 10 && <b className="pace-fast"> · running {ratio.toFixed(1)}× ahead of forecast</b>}
        </div>
      )}
    </>
  );
}

/**
 * When there's nothing to buy, the headline still says what to do: ask for a delivery sooner when the shelf empties
 * before it lands (same rule as To order's Ship sooner), chase an overdue delivery, or nothing at all.
 */
function NoOrderHeadline({ r, today }: { r: Row; today: string }) {
  if ((r.days_exposed ?? 0) > 0) {
    const { stock } = projectStock(r, today, 365);
    const first = stock.findIndex((v) => v <= 0.0001);
    const back = first >= 0 ? stock.findIndex((v, d) => d > first && v > 0.0001) : -1;
    const from = first <= 0 ? "now" : shortDate(addDays(today, first));
    const days = back > 0 ? back - first : Math.round(r.days_exposed ?? 0);
    return (
      <div className="big">
        <span className="badge late">Ship sooner</span>
        <span>
          Shelf empty from <b>{from}</b>{back > 0 && <> until <b>{shortDate(addDays(today, back))}</b></>} ({fmt(days)} days).
          Nothing new to order; ask the supplier to ship the next delivery sooner.
        </span>
      </div>
    );
  }
  const late = (r.arrivals ?? []).filter((a) => a.date && a.date < today);
  if (late.length) {
    return (
      <div className="big">
        <span className="badge late">Late PO</span>
        <span>Nothing new to order. A delivery is overdue ({fmt(late.reduce((t, a) => t + a.qty, 0))} {unitOf(r)}): chase the supplier.</span>
      </div>
    );
  }
  return <div className="big"><span className="muted">Nothing to order now. Stock and deliveries cover it.</span></div>;
}


function PartBody({ sku }: { sku: string }) {
  const s = useSupply();
  const { open } = usePanel();
  const r = s.bySku.get(sku);
  if (s.loading) return <><Head title={sku} /><div className="muted">Loading…</div></>;
  if (!r) return <><Head title={sku} /><div className="muted">This part isn't tracked here.</div></>;
  const unit = unitOf(r);
  const need = r.need_to_order ?? 0;
  const willUse = Math.round(r.lead_forecast ?? 0);
  const safety = Math.round(r.safety_qty ?? 0);
  const onHand = Math.round(r.supply.comp_available ?? 0);
  const inTime = Math.round(r.supply.incoming_within_lead ?? 0);
  const arrivals = r.arrivals ?? [];
  return (
    <>
      <Head title={sku} sub={partName(sku) || CATEGORY_LABEL[r.item_type]} />
      {need > 0 ? (
        <div className="big">
          <span className={`badge ${runsOut(r) ? "order" : "late"}`}>{runsOut(r) ? "Order now" : "Top-up"}</span>
          <span><b>{fmt(need)}</b> {unit} suggested</span>
        </div>
      ) : (
        <NoOrderHeadline r={r} today={s.today} />
      )}

      {need > 0 && (
        <div className="box">
          <h4>How {fmt(need)} is worked out</h4>
          <div className="sum">
            <span>Will use during the {r.lead_days ?? "—"}-day wait for new stock</span><b>{fmt(willUse)}</b>
            <span>+ Safety stock</span><b>{fmt(safety)}</b>
            <span>− On hand</span><b>{fmt(onHand)}</b>
            <span>− Coming in time</span><b>{fmt(inTime)}</b>
            <span className="tot">Need to order</span><b className="tot">{fmt(need)}</b>
          </div>
          {looksHigh(r) && (
            <div className="note-warn">
              <b>Check this one.</b> Safety stock ({fmt(safety)}) is more than it's expected to use ({fmt(willUse)}). Without it, the order would
              be {fmt(Math.max(0, willUse - onHand - inTime))}.
            </div>
          )}
        </div>
      )}

      <div className="box">
        <h4>Stock, next 6 months <span className="h4sub">estimate at the forecast rate</span></h4>
        <StockChart r={r} unit={unit} />
        {r.confidence && CONFIDENCE[r.confidence] && <div className="muted" style={{ marginTop: 6 }}>Forecast confidence: <b>{CONFIDENCE[r.confidence]}</b></div>}
      </div>

      <div className="box">
        <h4>On hand</h4>
        <b>{fmt(onHand)}</b> {unit}
        {(r.supply.fg_available ?? 0) > 0 && <span className="muted"> · plus {fmt(r.supply.fg_available)} inside built umbrellas</span>}
        <Sites sku={sku} type={r.item_type} f30={(r.demand?.forecasted ?? 0) / 3} />
        {r.days_cover_on_hand != null && <div className="muted" style={{ marginTop: 4 }}>Lasts about {Math.round(r.days_cover_on_hand)} days at the forecast rate</div>}
      </div>

      <div className="box">
        <h4>Arriving</h4>
        {arrivals.length === 0 ? (
          <span className="muted">Nothing on order</span>
        ) : (
          <div className="list">
            {arrivals.map((a, i) => {
              const late = a.date != null && a.date < s.today;
              return (
                <button key={i} type="button" className="lrow" onClick={() => open(a.source === "po" ? "po" : "ca", a.ref)}>
                  <span>
                    <b>{refLabel(a.ref, a.source)}</b>
                    <span className="s">
                      {a.window
                        ? late
                          ? `was due ${a.window}, not shipped yet`
                          : a.atRisk
                            ? `due ${a.window} · at risk, not on a container yet`
                            : a.date == null
                              ? `due ${a.window}`
                              : `due ${a.window} · about ${shortDate(a.date)}`
                        : a.date == null
                          ? "no date set"
                          : late
                            ? `this line was due ${shortDate(a.date)}`
                            : `${a.source === "container" ? "about " : ""}${shortDate(a.date)}`}
                    </span>
                  </span>
                  <span className="r">
                    {fmt(a.qty)} {unit}
                    {late && <span className="s"><span className="badge late">overdue</span></span>}
                    {a.atRisk && <span className="s"><span className="badge risk">at risk</span></span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="btnrow"><NetSuiteButton query={sku} /><CopyLink /></div>
      <p className="fine">Place POs in NetSuite as usual. Nothing here changes NetSuite.</p>
    </>
  );
}

// ─── PO or container ───────────────────────────────────────────

function DeliveryBody({ refId }: { refId: string }) {
  const s = useSupply();
  const d = s.deliveries.get(refId);
  const title = refId.startsWith("CA") ? refId : `PO ${refId}`;
  if (s.loading) return <><Head title={title} /><div className="muted">Loading…</div></>;
  if (!d) return <><Head title={title} /><div className="muted">Nothing open on this {refId.startsWith("CA") ? "container" : "PO"} for the parts tracked here.</div></>;
  return d.kind === "container" ? <ContainerBody d={d} /> : <PoBody d={d} />;
}

function LineList({ lines, showDate = true }: { lines: Line[]; showDate?: boolean }) {
  const { open } = usePanel();
  return (
    <div className="list">
      {lines.map((l, i) => (
        <button key={l.sku + i} type="button" className="lrow" onClick={() => open("part", l.sku)}>
          <span>
            <b>{l.sku}</b>
            {partName(l.sku) && <span className="s">{partName(l.sku)}</span>}
          </span>
          <span className="r">
            {fmt(l.qty)}{l.yards ? " yd" : ""}
            {showDate && <span className="s">{l.date ? `due ${shortDate(l.date)}` : "no date"}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

function PoBody({ d }: { d: Delivery }) {
  const [all, setAll] = useState(false);
  const unit = d.yards ? "yd" : "units";
  const lateUnits = d.late.reduce((t, l) => t + l.qty, 0);
  const laterShown = all ? d.later : d.later.slice(0, 3);
  const laterRange = d.later.length ? `${shortDate(d.later[0].date)} – ${shortDate(d.later[d.later.length - 1].date)}` : "";
  return (
    <>
      <Head
        title={`PO ${d.ref}`}
        sub={d.late.length ? <>{d.late.length} line{d.late.length > 1 ? "s" : ""} late · {fmt(lateUnits)} {unit} · oldest due {shortDate(d.oldestLate)}</> : <>{d.lines.length} open line{d.lines.length > 1 ? "s" : ""} · next due {shortDate(d.next)}</>}
      />
      {d.late.length > 0 && (
        <div className="big">
          <span className="badge late">{d.daysLate} days late</span>
          <span className="muted">No new date from the supplier</span>
        </div>
      )}
      {d.late.length > 0 && (
        <div className="box"><h4>Should be here now · {d.late.length} line{d.late.length > 1 ? "s" : ""}</h4><LineList lines={d.late} /></div>
      )}
      {d.stalled.length > 0 && (
        <div className="box"><h4>Stalled · {d.stalled.length} line{d.stalled.length > 1 ? "s" : ""} <span className="h4sub">over 30 days late, never shipped</span></h4><LineList lines={d.stalled} /></div>
      )}
      {d.later.length > 0 && (
        <div className="box">
          <h4>Not due yet · {d.later.length} line{d.later.length > 1 ? "s" : ""} <span className="h4sub">{laterRange}</span></h4>
          <LineList lines={laterShown} />
          {!all && d.later.length > 3 && <button type="button" className="btn link" style={{ marginTop: 8 }} onClick={() => setAll(true)}>Show all {d.later.length}</button>}
        </div>
      )}
      <div className="btnrow"><CopyForEmail d={d} /><NetSuiteButton query={`PO${d.ref}`} /><CopyLink /></div>
      <p className="fine">Only the parts this app tracks are listed. Nothing here changes NetSuite.</p>
    </>
  );
}

function CopyForEmail({ d }: { d: Delivery }) {
  const [copied, setCopied] = useState(false);
  const text = [
    `PO ${d.ref}: ${d.late.length} line(s) past due, oldest due ${shortDate(d.oldestLate)}.`,
    ...d.late.map((l) => `- ${l.sku}: ${fmt(l.qty)}${l.yards ? " yd" : ""}, due ${shortDate(l.date)}`),
    "Please send a new ship date.",
  ].join("\n");
  return <button type="button" className="btn" onClick={() => copy(text, setCopied)}>{copied ? "Copied" : "Copy for an email"}</button>;
}

function ContainerBody({ d }: { d: Delivery }) {
  const s = useSupply();
  const { params, setParams } = usePanel();
  const slip = Number(params.get("slip") ?? 0);
  const setSlip = (n: number) => {
    const next = new URLSearchParams(params);
    if (n) next.set("slip", String(n));
    else next.delete("slip");
    setParams(next, { replace: true });
  };
  const date = d.lines[0]?.date ?? null;
  const late = date != null && date < s.today;

  // What-if: which parts on this container would have more empty days if it came `slip` days later.
  const worse = slip
    ? d.lines
        .map((l) => s.bySku.get(l.sku))
        .filter((r): r is Row => !!r)
        .map((r) => {
          const base = emptyDays(r, s.today, d.ref, 0);
          const shifted = emptyDays(r, s.today, d.ref, slip);
          return { r, extra: shifted.days - base.days, firstEmpty: shifted.firstEmpty };
        })
        .filter((x) => x.extra > 0)
        .sort((a, b) => b.extra - a.extra)
    : [];

  return (
    <>
      <Head
        title={d.ref}
        sub={<>Container · {late ? <>was due at Jurupa {shortDate(date)} · <span className="late-t">{daysBetween(date!, s.today)} days late</span></> : <>at Jurupa about <b>{shortDate(date)}</b></>}</>}
      />
      <div className="box"><h4>All {d.lines.length} lines · {fmt(d.units)} units</h4><LineList lines={d.lines} showDate={false} /></div>
      {!late && (
        <div className="box">
          <h4>What if it's late?</h4>
          <div className="whatif">
            {[7, 14, 21].map((n) => (
              <button key={n} type="button" className={`btn${slip === n ? " primary" : ""}`} aria-pressed={slip === n} onClick={() => setSlip(slip === n ? 0 : n)}>
                +{n / 7} week{n > 7 ? "s" : ""}
              </button>
            ))}
          </div>
          {slip > 0 && (
            <div className="note-warn">
              {worse.length === 0
                ? <>No part on it would run out because of a {slip / 7}-week delay.</>
                : worse.map((x) => (
                    <div key={x.r.itemid}>
                      <b>{x.r.itemid}</b> would be empty about <b>{x.extra} more days</b>{x.firstEmpty ? <> (from about {shortDate(x.firstEmpty)})</> : null}.
                    </div>
                  ))}
            </div>
          )}
          <p className="fine" style={{ margin: "8px 0 0" }}>An estimate only. Nothing is changed.</p>
        </div>
      )}
      <div className="btnrow"><NetSuiteButton query={d.ref} label="Receive in NetSuite" primary /><CopyLink /></div>
      <p className="fine">Container dates are the ship's arrival + 9 days to reach Jurupa. Nothing here changes NetSuite.</p>
    </>
  );
}
